import React, { useState } from "react";
import styled from "styled-components";
import {
  SectionRibbon,
  SegmentedPicker,
  GlassCard,
  Badge,
} from "../../../../ui";
import { GenerateConfigEDU } from "./_helpers/GenerateConfigEDU";
import { GenerateConfigMag } from "./_helpers/GenerateConfigMag";
import { GenerateConfigLog } from "./_helpers/GenerateConfigLog";

export const ConfigurationSettings = ({
  system,
  configEDU,
  configMag,
  configLog,
  regexModels,
}) => {
  const [activeConfig, setActiveConfig] = useState("EDU");

  const configOptions = [
    {
      eventKey: "EDU",
      label: "ENV Config",
      description: "ENV Data Configuration",
      hasConfig: !!configEDU,
    },
    {
      eventKey: "MAG",
      label: "MAG Config",
      description: "Magnetic Resonance Configuration",
      hasConfig: configMag?.length > 0,
    },
    {
      eventKey: "LOG",
      label: "LOG Config",
      description: "System Log Configuration",
      hasConfig: configLog?.length > 0,
    },
  ];

  const getConfiguredCount = () => {
    return configOptions.filter((option) => option.hasConfig).length;
  };

  const getOverallStatus = () => {
    const configuredCount = getConfiguredCount();
    if (configuredCount === 0)
      return { variant: "error", text: "No Configurations" };
    if (configuredCount === configOptions.length)
      return { variant: "success", text: "All Configured" };
    return {
      variant: "warning",
      text: `${configuredCount}/${configOptions.length} Configured`,
    };
  };

  const renderActiveConfiguration = () => {
    switch (activeConfig) {
      case "EDU":
        return (
          <GenerateConfigEDU config={configEDU} regex_models={regexModels} />
        );
      case "MAG":
        return (
          <GenerateConfigMag
            configs={configMag || []}
            regex_models={regexModels}
            system={system}
          />
        );
      case "LOG":
        return (
          <GenerateConfigLog
            configs={configLog || []}
            regex_models={regexModels}
          />
        );
      default:
        return (
          <GenerateConfigEDU config={configEDU} regex_models={regexModels} />
        );
    }
  };

  const activeConfigData = configOptions.find(
    (opt) => opt.eventKey === activeConfig,
  );
  const overallStatus = getOverallStatus();

  return (
    <ConfigContainer>
      <SectionRibbon
        title={`System Configuration - ${getConfiguredCount()} of ${configOptions.length} configured`}
        variant="primary"
        rightContent={
          <Badge variant={overallStatus.variant}>{overallStatus.text}</Badge>
        }
      />

      <ConfigOverview>
        <OverviewGrid>
          {configOptions.map((option) => (
            <OverviewCard
              key={option.eventKey}
              isActive={option.eventKey === activeConfig}
              hasConfig={option.hasConfig}
              onClick={() => setActiveConfig(option.eventKey)}
            >
              <CardIcon>{option.icon}</CardIcon>
              <CardContent>
                <CardTitle>{option.label}</CardTitle>
                <CardDescription>{option.description}</CardDescription>
                <CardStatus>
                  <StatusBadge variant={option.hasConfig ? "success" : "error"}>
                    {option.hasConfig ? "Configured" : "Not Configured"}
                  </StatusBadge>
                </CardStatus>
              </CardContent>
            </OverviewCard>
          ))}
        </OverviewGrid>
      </ConfigOverview>

      <NavigationSection>
        <NavigationContainer>
          <SegmentedPicker
            options={configOptions}
            activeKey={activeConfig}
            onSelect={setActiveConfig}
            size="medium"
          />
        </NavigationContainer>
      </NavigationSection>

      <ConfigContent>
        <GlassCard>
          <ConfigHeader>
            <ConfigHeaderContent>
              <ConfigIcon>{activeConfigData?.icon}</ConfigIcon>
              <ConfigHeaderText>
                <ConfigTitle>
                  {activeConfigData?.label} Configuration
                </ConfigTitle>
                <ConfigSubtitle>{activeConfigData?.description}</ConfigSubtitle>
              </ConfigHeaderText>
              <ConfigHeaderStatus>
                <StatusBadge
                  variant={activeConfigData?.hasConfig ? "success" : "error"}
                >
                  {activeConfigData?.hasConfig
                    ? "Configured"
                    : "Not Configured"}
                </StatusBadge>
              </ConfigHeaderStatus>
            </ConfigHeaderContent>
          </ConfigHeader>

          <ConfigBody>{renderActiveConfiguration()}</ConfigBody>
        </GlassCard>
      </ConfigContent>

      <ConfigurationInfo>
        <GlassCard>
          <InfoContent>
            <InfoHeader>
              <InfoTitle>Configuration Guidelines</InfoTitle>
            </InfoHeader>

            <InfoGrid>
              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>ENV Configuration</InfoItemTitle>
                  <InfoItemText>
                    Configure ENV data extraction parameters, file patterns, and
                    processing rules for academic systems.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>MAG Configuration</InfoItemTitle>
                  <InfoItemText>
                    Set up magnetic resonance data processing settings, DICOM
                    parameters, and imaging protocols.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>LOG Configuration</InfoItemTitle>
                  <InfoItemText>
                    Configure system logging parameters, retention policies, and
                    monitoring thresholds.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>
            </InfoGrid>

            <ImportantNote>
              <NoteIcon>⚠️</NoteIcon>
              <NoteText>
                Configuration changes may require system restart to take effect.
                Always test configurations in a development environment first.
              </NoteText>
            </ImportantNote>
          </InfoContent>
        </GlassCard>
      </ConfigurationInfo>
    </ConfigContainer>
  );
};

// Styled Components
const ConfigContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ConfigOverview = styled.div`
  margin-bottom: 1rem;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const OverviewCard = styled.div`
  padding: 1.5rem;
  background: ${({ isActive, hasConfig }) => {
    if (isActive && hasConfig) return "rgba(52, 152, 219, 0.1)";
    if (isActive && !hasConfig) return "rgba(255, 193, 7, 0.1)";
    return hasConfig ? "rgba(39, 174, 96, 0.05)" : "rgba(231, 76, 60, 0.05)";
  }};
  border: 2px solid
    ${({ isActive, hasConfig }) => {
      if (isActive && hasConfig) return "rgba(52, 152, 219, 0.3)";
      if (isActive && !hasConfig) return "rgba(255, 193, 7, 0.3)";
      return hasConfig ? "rgba(39, 174, 96, 0.2)" : "rgba(231, 76, 60, 0.2)";
    }};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const CardDescription = styled.p`
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
`;

const CardStatus = styled.div`
  margin-top: 0.25rem;
`;

const StatusBadge = styled(Badge)`
  font-size: 0.75rem;
`;

const NavigationSection = styled.div`
  display: flex;
  justify-content: center;
  margin: 1rem 0;
`;

const NavigationContainer = styled.div`
  display: flex;
`;

const ConfigContent = styled.div`
  width: 100%;
`;

const ConfigHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const ConfigHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ConfigIcon = styled.div`
  font-size: 2rem;
`;

const ConfigHeaderText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ConfigTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const ConfigSubtitle = styled.p`
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
`;

const ConfigHeaderStatus = styled.div`
  flex-shrink: 0;
`;

const ConfigBody = styled.div`
  padding: 2rem;
`;

const ConfigurationInfo = styled.div`
  margin-top: 2rem;
`;

const InfoContent = styled.div`
  padding: 2rem;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 4px solid #3498db;
`;

const InfoItemIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const InfoItemContent = styled.div`
  flex: 1;
`;

const InfoItemTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoItemText = styled.p`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
  line-height: 1.5;
`;

const ImportantNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 8px;
  border-left: 4px solid #ffc107;
`;

const NoteIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

const NoteText = styled.div`
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.5;
  font-weight: 500;
`;
